import Lightbox, { type ZoomRef } from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { Box, Flex } from '@chakra-ui/react';
import type { PropsWithChildren } from 'react';
import {
  CloseIcon,
  Download2Icon,
  toaster,
  tools,
  Tooltip,
  Zoom,
  ZoomInIcon,
  ZoomOutIcon,
} from '@halo/ui-chat-react';
import { downloadImage } from '@halo/utils';
import dayjs from 'dayjs';

interface Props {
  slides: any[];
  open: boolean;
  onClose: () => void;
}
const IconButton = memo(function IconButton({
  children,
  onClick,
  reverse,
  alt,
}: PropsWithChildren<{ onClick?: () => void; reverse?: boolean; alt?: string }>) {
  const buttonContent = (
    <Box
      fontSize="lg"
      boxSize="34px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      onClick={onClick}
      transform={reverse === undefined || reverse === false ? 'scaleX(-1)' : undefined}
      overflow="hidden"
    >
      {children}
    </Box>
  );

  if (!alt) return buttonContent;

  return (
    <Tooltip
      showArrow={true}
      content={alt}
      contentProps={{
        style: { zIndex: 9999 },
      }}
    >
      {buttonContent}
    </Tooltip>
  );
});
const Header = memo(function Header({
  onDownload,
  onClose,
  onZoomIn,
  onZoomOut,
}: {
  onDownload?: () => void;
  onClose?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box position="absolute" top={0} left={0} right={0} w="full" pointerEvents="auto">
      <Flex
        w="full"
        justify="flex-end"
        alignItems="center"
        px={4}
        background="linear-gradient(0deg, rgba(0, 0, 0, 0) 10%, rgba(0, 0, 0, 1) 164.17%)"
        color="black"
        height="60px"
        overflow="hidden"
      >
        <Flex gap="2.5">
          <Box p="1" bg="rgba(0, 0, 0, 0.5)" borderRadius="34px" backdropFilter="blur(5px)">
            <Flex gap={1}>
              <IconButton onClick={onZoomOut} alt={t('tips.zoomOut')}>
                <ZoomOutIcon color="white" />
              </IconButton>
              <IconButton onClick={onZoomIn} alt="Zoom In">
                <ZoomInIcon color="white" />
              </IconButton>
              <IconButton onClick={onDownload} alt="Download">
                <Download2Icon color="white" />
              </IconButton>
            </Flex>
          </Box>
          <Box p="1" bg="rgba(0, 0, 0, 0.5)" borderRadius="34px" backdropFilter="blur(5px)">
            <Flex gap={1}>
              <IconButton onClick={onClose} alt={t('tips.close')}>
                <CloseIcon color="white" />
              </IconButton>
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
});
const PreviewImage = (props: Props) => {
  const { slides, open, onClose } = props;
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();
  const handleDownload = useCallback(() => {
    if (!slides[currentIndex].src) {
      toaster.error({ title: t('tips.imageNotLoaded') });
      return;
    }
    downloadImage(slides[currentIndex].src, `${dayjs().format('YYYYMMDDHHmmss')}.png`)
      .then(() => {
        tools.toaster.success({ title: t('tips.downloadSuccess') });
      })
      .catch(() => {
        tools.toaster.error({ title: t('tips.downloadFailed') });
      });
  }, [currentIndex, slides, t]);

  const zoomRef = useRef<ZoomRef>(null);

  const handleZoomIn = useCallback(() => {
    zoomRef.current?.zoomIn();
  }, [zoomRef]);

  const handleZoomOut = useCallback(() => {
    zoomRef.current?.zoomOut();
  }, [zoomRef]);

  const [disabled, setDisabled] = useState<boolean>(true);

  return (
    <Box
      position="absolute"
      draggable={false}
      top={0}
      left={0}
      w="full"
      h="full"
      onContextMenu={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Lightbox
        index={currentIndex}
        open={open}
        close={onClose}
        slides={slides}
        plugins={[Zoom]}
        zoom={{
          ref: zoomRef,
          maxZoomPixelRatio: 5,
          minZoom: 0.1,
          zoomInMultiplier: 2,
          scrollToZoom: true,
          wheelZoomStepRatio: 0.1,
          disabled,
        }}
        carousel={{ imageFit: 'contain', finite: true }}
        animation={{ fade: 250, swipe: 250 }}
        controller={{ closeOnBackdropClick: true }}
        styles={{
          container: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
          controls: () => {
            return (
              <Header
                onDownload={handleDownload}
                onClose={onClose}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
              />
            );
          },
        }}
        on={{
          view: ({ index }) => {
            setCurrentIndex(index);
          },
          entered: () => {
            setDisabled(false);
          },
          exited: () => {
            setDisabled(true);
          },
          entering: () => {
            setDisabled(true);
          },
          exiting: () => {
            setDisabled(true);
          },
        }}
      />
    </Box>
  );
};
export default memo(PreviewImage);
